import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import GratitudeCard from "../components/GratitudeCard";
import MatchedPairs from "../components/MatchedPairs";
import GratitudeTimeline from "../components/GratitudeTimeline";
import PublicAddGratitudeDialog from "../components/PublicAddGratitudeDialog";
import { Button } from "../components/ui/button";
import { HeartHandshake, Plus, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "../App";

const API = "/api";

// Stagger children on mount for a warm cascading reveal
const gridVariants = {
  show: {
    transition: { staggerChildren: 0.07, delayChildren: 0.08 },
  },
};
const cardVariants = {
  hidden: { opacity: 0, y: 16, scale: 0.98 },
  show: {
    opacity: 1, y: 0, scale: 1,
    transition: { type: "spring", stiffness: 180, damping: 20 },
  },
};

// Hand-drawn SVG underline that draws itself on mount (from framer-motion)
const HandUnderline = ({ className = "", stroke = "#8b5cf6" }) => (
  <svg viewBox="0 0 120 14" fill="none" xmlns="http://www.w3.org/2000/svg"
    className={className} preserveAspectRatio="none">
    <motion.path
      d="M2 10 Q30 2 60 7 T118 5"
      stroke={stroke} strokeWidth={3} strokeLinecap="round"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
      transition={{ duration: 1.1, ease: "easeOut" }}
    />
  </svg>
);

const PublicGratitudeWallPage = () => {
  // Route may be /u/:slug/gratitude OR /:username/gratitude — backend resolver
  // accepts either. Derive a single `identifier` for the API calls and the
  // SEO canonical path.
  const { slug, username } = useParams();
  const identifier = username || slug;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  // "Create your own wall" → skip the landing page. If already signed in,
  // jump to the dashboard; otherwise kick off Google OAuth and come back to
  // /dashboard post-auth so the new user lands on their own wall immediately.
  const startOwnWall = () => {
    if (user) {
      navigate("/dashboard");
      return;
    }
    const returnUrl = encodeURIComponent("/dashboard");
    window.location.href = `/api/auth/google/login?return_url=${returnUrl}`;
  };
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [wheelAreaMap, setWheelAreaMap] = useState({});
  const [addOpen, setAddOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [res, wheelRes] = await Promise.all([
        fetch(`${API}/public/gratitude/${encodeURIComponent(identifier)}`),
        fetch(`${API}/tools/wheel/areas`),
      ]);
      if (res.status === 404) {
        setNotFound(true);
        return;
      }
      if (res.ok) setData(await res.json());
      if (wheelRes.ok) {
        const w = await wheelRes.json();
        const map = {};
        (w.default_areas || []).forEach((a) => { map[a.key] = a; });
        (w.suggested_areas || []).forEach((a) => { map[a.key] = a; });
        setWheelAreaMap(map);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [identifier]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50 px-6 text-center">
        <SEO title="Gratitude wall not found" description="This wall doesn't exist or is private" noIndex />
        <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mb-4">
          <HeartHandshake className="w-8 h-8 text-violet-400" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">This wall isn&apos;t public</h1>
        <p className="text-slate-600 mb-6">
          The link might be broken, or the owner keeps their gratitude private.
        </p>
        <Button
          onClick={startOwnWall}
          className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
        >
          Start your own wall
        </Button>
      </div>
    );
  }

  const first = data.owner?.first_name || "Someone";
  const selfCards = (data.cards || []).filter((c) => c.type === "self");
  const receivedCards = (data.cards || []).filter((c) => c.type === "offered");

  const renderCard = (card, idx) => (
    <motion.div
      key={card.card_id}
      variants={cardVariants}
      style={{ breakInside: "avoid", marginBottom: 16, display: "block" }}
      whileHover={{ y: -3, rotate: (idx % 2 === 0 ? 0.4 : -0.4), transition: { duration: 0.25 } }}
    >
      <GratitudeCard
        card={{
          ...card,
          perspective: card.type === "self" ? "mine" : "received",
          visibility: "public",
        }}
        readOnly
        wheelAreaMap={wheelAreaMap}
      />
    </motion.div>
  );

  const Section = ({ title, subtitle, count, cards, empty, dotColor = "#8b5cf6" }) => (
    <div className="mb-12">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              {subtitle}
            </p>
          </div>
          <h2 className="relative inline-block text-xl sm:text-2xl font-bold text-slate-900">
            {title}
            <span className="absolute left-0 -bottom-2 w-full h-3 pointer-events-none">
              <HandUnderline className="w-full h-full" stroke={dotColor} />
            </span>
          </h2>
        </div>
        <span className="text-xs text-slate-400 font-medium">{count}</span>
      </div>

      {cards.length === 0 ? (
        <div className="py-10 text-center rounded-2xl bg-white/60 border border-dashed border-slate-200">
          <p className="text-sm text-slate-500 italic">{empty}</p>
        </div>
      ) : (
        <motion.div
          variants={gridVariants}
          initial="hidden"
          animate="show"
          style={{ columnGap: 16, columnCount: 1 }}
          className="gratitude-masonry"
        >
          {cards.map((c, i) => renderCard(c, i))}
        </motion.div>
      )}
    </div>
  );

  return (
    <>
      <SEO
        title={`${first}'s Gratitude Wall`}
        description={`${first}'s wall of gratitude — ${data.stats.total} note${data.stats.total !== 1 ? "s" : ""} worth slowing down for.`}
        path={data.owner?.username ? `/${data.owner.username}/gratitude` : `/u/${slug}/gratitude`}
      />

      <div
        className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50 relative overflow-hidden"
        data-testid="public-gratitude-page"
      >
        {/* Ambient floating dots — very subtle background texture */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          {[...Array(14)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-violet-300"
              style={{
                left: `${(i * 13 + 7) % 100}%`,
                top: `${(i * 31 + 11) % 100}%`,
                opacity: 0.08,
              }}
              animate={{ y: [0, -10, 0] }}
              transition={{
                duration: 6 + (i % 3),
                repeat: Infinity,
                ease: "easeInOut",
                delay: (i % 5) * 0.5,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          {/* ── HERO ───────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            {data.owner?.picture && (
              <motion.img
                src={data.owner.picture}
                alt={data.owner.name}
                className="w-20 h-20 rounded-full object-cover mx-auto mb-4 ring-2 ring-violet-100 shadow-sm"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: [0.9, 1.02, 1], opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            )}
            <span className="text-[11px] font-semibold uppercase tracking-widest text-violet-600 block mb-2">
              A wall of gratitude
            </span>
            <h1
              className="text-3xl sm:text-5xl font-bold text-slate-900 mb-3 leading-tight"
              data-testid="public-wall-title"
            >
              <span className="text-violet-600">{first}</span>'s Gratitude Wall
            </h1>
            <p className="text-sm text-slate-500 mb-7">
              {data.stats.total} public note{data.stats.total !== 1 ? "s" : ""} · kept close, shared gently
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5">
              <Button
                onClick={() => setAddOpen(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6 py-5 text-sm font-semibold shadow-lg hover:shadow-xl transition-shadow"
                data-testid="public-wall-add-cta"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Add gratitude for {first}
              </Button>
              <Button
                onClick={startOwnWall}
                variant="ghost"
                className="text-violet-700 hover:text-violet-800 hover:bg-violet-50 rounded-full px-5 py-5 text-sm font-medium"
                data-testid="public-wall-create-own"
              >
                Create your own wall
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </motion.div>

          {/* ── CONTENT ─────────────────────────────────────────────── */}
          {data.cards.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="py-16 text-center rounded-2xl bg-white border border-dashed border-violet-200 max-w-xl mx-auto"
            >
              <Sparkles className="w-8 h-8 text-violet-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-1 font-medium">
                {first} hasn&apos;t shared any public notes yet.
              </p>
              <p className="text-xs text-slate-400">
                Be the first to leave a note for them.
              </p>
            </motion.div>
          ) : (
            <>
              {/* Gratitude Rhythm — public read-only timeline.
                  Uses `timeline_counts` (metadata-only) so the ribbon reflects
                  the *full* shape of the user's gratitude practice including
                  private cards — while card content stays private. Falls back
                  to `cards` for older API payloads. */}
              <GratitudeTimeline
                cards={
                  Array.isArray(data.timeline_counts)
                    ? data.timeline_counts
                    : (data.cards || []).map((c) => ({
                        ...c,
                        perspective: c.type === "self" ? "mine" : "received",
                      }))
                }
                wheelAreaMap={wheelAreaMap}
                variant="profile"
                ownerFirstName={first}
                joinedAt={data.owner?.joined_at}
                joinedPicture={data.owner?.picture}
              />

              {/* Mutual Gratitude — front-and-center proof of warm, reciprocal
                  relationships on this wall. Hidden if there are no matches. */}
              <MatchedPairs
                matches={data.matches || []}
                viewerName={first}
                wheelAreaMap={wheelAreaMap}
                readOnly
              />

              <Section
                title={`What ${first} is grateful for`}
                subtitle="Their own reflections"
                count={`${selfCards.length} note${selfCards.length !== 1 ? "s" : ""}`}
                cards={selfCards}
                empty={`${first} hasn't shared public self-reflections yet.`}
                dotColor="#8b5cf6"
              />
              <Section
                title={`Reasons people feel grateful for ${first}`}
                subtitle="From their Circle"
                count={`${receivedCards.length} note${receivedCards.length !== 1 ? "s" : ""}`}
                cards={receivedCards}
                empty={"No public notes from others yet — you could be the first."}
                dotColor="#ec4899"
              />
            </>
          )}

          {/* ── End-of-wall CTA ─────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-16 mb-6 max-w-2xl mx-auto"
          >
            <div
              className="rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden backdrop-blur-md"
              style={{
                background: "linear-gradient(135deg, rgba(167,139,250,0.18) 0%, rgba(244,114,182,0.14) 100%)",
                border: "1px solid rgba(167,139,250,0.30)",
                transform: "rotate(-0.5deg)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
              }}
              data-testid="public-wall-end-cta-card"
            >
              <Sparkles className="w-10 h-10 text-violet-300 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-white mb-2">
                You could have a wall like this.
              </h3>
              <p className="text-sm mb-5 max-w-sm mx-auto" style={{ color: "rgba(255,255,255,0.78)" }}>
                Walls like {first}'s take about 45 seconds to start.
                Build your own — small notes today, a beautiful archive tomorrow.
              </p>
              <Button
                onClick={startOwnWall}
                className="bg-violet-500 hover:bg-violet-400 rounded-full px-6 py-5 text-sm font-semibold"
                style={{ color: "#0c0c0c" }}
                data-testid="public-wall-end-cta"
              >
                Build my gratitude wall
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      <PublicAddGratitudeDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        ownerName={data.owner.name}
        ownerFirstName={first}
        slug={identifier}
        onSuccess={fetchData}
      />

      <style>{`
        @media (min-width: 640px) { .gratitude-masonry { column-count: 2 !important; } }
        @media (min-width: 1024px) { .gratitude-masonry { column-count: 3 !important; } }
      `}</style>
    </>
  );
};

export default PublicGratitudeWallPage;
